import { observer } from 'mobx-react-lite'
import { ValidationContext } from 'validx'
import {
  FormField,
  FormFieldError,
  FormFieldLabel,
  FormFieldHint,
  useFormField,
  type FormProps,
  Form,
} from '@/components/Form'
import { type ReactNode, createContext, useContext } from 'react'
import type { FormControlProps } from '@/infra/prop-types'
import type { FilterProps } from '@/infra/type-utils'

interface FormContainerProps extends FormProps {
  /**
   * When disabled, removes the submit handler and
   * propagates the disabled state to the form field containers.
   */
  disabled?: boolean
}

/**
 * Form container allows disabling all controls in the form at the same time.
 */
export const FormContainer = observer(function FormContainer({
  disabled,
  onSubmit,
  children,
  ...rest
}: FormContainerProps) {
  return (
    <FormDisabledContext.Provider value={disabled}>
      <Form {...rest} onSubmit={disabled ? undefined : onSubmit}>
        {children}
      </Form>
    </FormDisabledContext.Provider>
  )
})

/**
 * Like {@link FormContainer} but does not have any styling applied, it's
 * just a basic `<form>`.
 */
export const UnstyledFormContainer = observer(function UnstyledFormContainer({
  disabled,
  onSubmit,
  children,
  ...rest
}: FormContainerProps) {
  return (
    <FormDisabledContext.Provider value={disabled}>
      <form
        {...rest}
        style={{
          // Ok people calm down, put down your pitchforks, I know it
          // says "unstyled form container", but in order for the presence
          // of this form to not affect layout, it needs `display: contents`,
          // that way it becomes truly unstyled.
          display: 'contents',
        }}
        onSubmit={disabled ? undefined : onSubmit}
      >
        {children}
      </form>
    </FormDisabledContext.Provider>
  )
})

/**
 * Properties for a control container component.
 *
 * `M` = the model type, must be an object.
 * `F` = the field type
 * `K` = the field name (key) of the object
 * `P` = control props type, should omit the values being bound
 *  by the container.
 */
export type ContainerProps<
  M extends object,
  F,
  K extends keyof FilterProps<M, F>,
  P,
> = P &
  FormFieldProps & {
    model: M
    field: K
  }

/**
 * Props for a form field container component which composes the field.
 */
interface FormFieldContainerProps<
  M extends object,
  K extends string & keyof M,
> extends FormFieldProps {
  model: M
  field: K
  render: (controlProps: FormControlProps) => ReactNode
}

/**
 * Props being passed to the form field components.
 */
interface FormFieldProps {
  name?: string
  label?: ReactNode
  hint?: ReactNode
  'aria-label'?: string

  /**
   * When `false`, the field and control will appear as valid even if
   * found in the validation context, and will not show the invalid state.
   * When `true`, shows the invalid state but does not render an
   * error in the hint, even if one exists in the validation context.
   */
  error?: ReactNode

  /**
   * Test ID attribute for the field hint.
   */
  hintTestId?: string
}

/**
 * Form field container that renders all components that are necessary for
 * a form field. The actual control must be rendered by the `render` prop.
 */
export const FormFieldContainer = observer(function FormFieldContainer<
  M extends object,
  K extends string & keyof M,
>({
  label,
  hint,
  name,
  error,
  model,
  field,
  render,
  hintTestId,
  'aria-label': ariaLabel,
}: FormFieldContainerProps<M, K>) {
  const {
    controlProps,
    errorContent,
    errorProps,
    hintProps,
    hintContent,
    labelProps,
  } = useFormFieldContainer({
    name,
    model,
    field,
    hint,
    error,
    'aria-label': ariaLabel,
  })

  return (
    <FormField>
      {label && <FormFieldLabel {...labelProps}>{label}</FormFieldLabel>}

      {render(controlProps)}
      <FormFieldHint testId={hintTestId} {...hintProps}>
        {hintContent}
      </FormFieldHint>
      <FormFieldError testId={hintTestId} {...errorProps}>
        {errorContent}
      </FormFieldError>
    </FormField>
  )
})

interface UseFormFieldContainerProps<
  M extends object,
  K extends string & keyof M,
> {
  name?: string
  model: M
  field: K
  'aria-label'?: string
  error?: ReactNode
  hint?: ReactNode
}

/**
 * Wrapper for the form field hook
 */
export function useFormFieldContainer<
  M extends object,
  K extends string & keyof M,
>({
  name,
  model,
  field,
  error,
  hint,
  'aria-label': ariaLabel,
}: UseFormFieldContainerProps<M, K>) {
  error ??= getValidationContext(model)?.getError(field)

  const invalid = Boolean(error)
  const errorContent = error === true ? null : error
  const hintContent = hint
  const formDisabled = useFormDisabled()
  const {
    control: controlProps,
    error: errorProps,
    hint: hintProps,
    label: labelProps,
  } = useFormField({
    'aria-label': ariaLabel,
    name: name || field,
    invalid,
    errorMessage: errorContent,
    hint: hintContent,
  })

  return {
    errorContent,
    errorProps,
    hintProps,
    labelProps,
    hintContent,
    controlProps:
      formDisabled === undefined
        ? controlProps
        : { ...controlProps, disabled: formDisabled },
  }
}

const FormDisabledContext = createContext<boolean | undefined>(undefined)

/**
 * Gets whether a parent form is disabled.
 */
export function useFormDisabled() {
  return useContext(FormDisabledContext)
}

/**
 * Gets a validation context on the given model under the well-known
 * `validation` property name, if one exists.
 *
 * @param model
 */
function getValidationContext<M extends object>(
  model: M,
): ValidationContext | null {
  if ('validation' in model && model.validation instanceof ValidationContext) {
    return model.validation
  }

  return null
}
